import { type CSSProperties } from "react";
import { Notify } from "@/components/ui/notify";
import { useProductionOrderIndicators } from "./hooks";
import {
  OeeHero,
  Tile,
  TileGrid,
  type TileSpec,
  clockLabel,
  delayTile,
  downtimeTile,
  dueTile,
  minLabel,
  pct,
  scrapTile,
} from "./indicators-parts";
import { formatQuantity } from "@/lib/quantity-format";

// Indicadores por OP, parcial por estado:
// SCHEDULED → só planejado; RUNNING → OEE PARCIAL ao vivo (hero + A/P/Q + progresso + tiles);
// COMPLETED → OEE consolidado sobre a janela real. Partes visuais em `indicators-parts`.
export function ProductionOrderIndicators({ orderId }: { orderId: string }) {
  const { data, isLoading, isError, error, refetch } = useProductionOrderIndicators(orderId);
  if (isError) return <Notify.QueryError error={error} onRetry={() => void refetch()} />;
  if (isLoading || !data)
    return <div className="p-6 text-sm text-text-tertiary">Carregando indicadores…</div>;
  const { status, planned, due, execution } = data;
  const dueSpec = dueTile(due);

  if (status === "SCHEDULED" || !execution) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Tile label="Qtd planejada" value={`${formatQuantity(planned.plannedQty)} un`} />
          <Tile label="Duração planejada" value={minLabel(planned.plannedMin)} />
          {dueSpec ? <Tile {...dueSpec} /> : null}
        </div>
        <p className="text-sm text-text-tertiary">
          Indicadores de execução ficam disponíveis quando a ordem inicia/conclui.
        </p>
      </div>
    );
  }

  if (status === "RUNNING") {
    const prog =
      execution.producedQty != null && planned.plannedQty > 0
        ? Math.round((execution.producedQty / planned.plannedQty) * 100)
        : 0;
    const tiles: TileSpec[] = [
      {
        label: "Decorrido",
        value: minLabel(execution.actualDurationMin),
        sub: `prevista ${minLabel(planned.plannedMin)}`,
      },
      { label: "Fim previsto", value: clockLabel(planned.expectedEnd), sub: "planejado" },
      delayTile(execution.startDelayMin),
      downtimeTile(execution.downtimeMin),
      scrapTile(execution.scrapPct),
      {
        label: "Aderência ao plano",
        value: pct(execution.planAdherence),
        sub: "ritmo vs cadência",
      },
      ...(dueSpec ? [dueSpec] : []),
    ];
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">Progresso</span>
            <span className="font-medium">
              {formatQuantity(execution.producedQty ?? 0)} / {formatQuantity(planned.plannedQty)} un
              · {prog}%
            </span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-bg-sunken">
            <div
              style={{ "--p": `${Math.min(prog, 100)}%` } as CSSProperties}
              className="h-full w-[var(--p)] rounded-full bg-rd-success-500"
            />
          </div>
        </div>

        <OeeHero
          oee={execution.oee}
          availability={execution.availability}
          performance={execution.performance}
          quality={execution.quality}
          live
        />

        <TileGrid tiles={tiles} />

        <p className="text-xs text-text-tertiary">
          OEE parcial — recalculado sobre o tempo já decorrido; consolida no encerramento da run.
        </p>
      </div>
    );
  }

  // COMPLETED
  const prodPct =
    execution.producedQty != null && planned.plannedQty > 0
      ? ((execution.producedQty / planned.plannedQty) * 100).toFixed(1)
      : null;
  const tiles: TileSpec[] = [
    {
      label: "Produzido vs planejado",
      value: pct(prodPct == null ? null : Number(prodPct)),
      sub: `${formatQuantity(execution.producedQty ?? 0)} / ${formatQuantity(planned.plannedQty)} un`,
      tone: "text-rd-success-700",
    },
    {
      label: "Aderência ao plano",
      value: pct(execution.planAdherence),
      sub: "ritmo vs cadência planejada",
      tone: "text-rd-success-700",
    },
    scrapTile(execution.scrapPct),
    downtimeTile(execution.downtimeMin),
    delayTile(execution.startDelayMin, "text-rd-success-700"),
    {
      label: "Duração real",
      value: minLabel(execution.actualDurationMin),
      sub: `planejada ${minLabel(planned.plannedMin)}`,
    },
    ...(dueSpec ? [dueSpec] : []),
  ];
  return (
    <div className="space-y-4">
      <OeeHero
        oee={execution.oee}
        availability={execution.availability}
        performance={execution.performance}
        quality={execution.quality}
      />
      <TileGrid tiles={tiles} />
    </div>
  );
}
